import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { getCurrentUser } from '@/api/auth'
import { getToken } from '@/utils/auth'
import {
    getClientType,
    getClientTypeByRoutePath,
    getHomePathByRole,
    getLoginPathByRoutePath
} from '@/utils/device'

import Login from '@/views/login/index.vue'
import AdminLayout from '@/layout/AdminLayout.vue'
import AdminStudent from '@/views/admin/student/index.vue'
import AdminLoginLog from '@/views/admin/login-log/index.vue'
import AdminQuestion from '@/views/admin/question/index.vue'
import AdminHome from '@/views/admin/home/index.vue'

import StudentLayout from '@/views/student/layout.vue'
import StudentHome from '@/views/student/home.vue'

import MobileLogin from '@/views/mobile/login/index.vue'
import MobileStudentLayout from '@/views/mobile/layout/MobileStudentLayout.vue'
import MobileAdminLayout from '@/views/mobile/layout/MobileAdminLayout.vue'
import MobileStudentHome from '@/views/mobile/student/home.vue'
import MobileStudentPlaceholder from '@/views/mobile/student/placeholder.vue'
import MobileStudentMe from '@/views/mobile/student/me.vue'
import MobileAdminStudent from '@/views/mobile/admin/student/index.vue'
import MobileAdminLearningAnalysis from '@/views/mobile/admin/learningAnalysis/index.vue'
import MobileAdminMe from '@/views/mobile/admin/me.vue'

Vue.use(VueRouter)

const routes = [
    {
        path: '/',
        redirect: () => {
            return getClientType() === 'mobile' ? '/m/login' : '/login'
        }
    },
    // ================= PC  =================
    {
        path: '/login',
        name: 'Login',
        component: Login
    },
    // ================= 移动端 =================
    {
        path: '/m/login',
        name: 'MobileLogin',
        component: MobileLogin
    },
    // ================= PC 后台 =================
    {
        path: '/admin',
        component: AdminLayout,
        redirect: '/admin/home',
        children: [
            {
                path: 'home',
                name: 'AdminHome',
                component: AdminHome
            },
            {
                path: 'student',
                name: 'AdminStudent',
                component: AdminStudent
            },
            {
                path: 'learning-analysis',
                name: 'AdminLearningAnalysis',
                component: () => import('@/views/admin/learningAnalysis/index.vue')
            },
            // ✨✨✨ 新增：教学建议 (PC端) ✨✨✨
            {
                path: 'teaching-suggestion',
                name: 'AdminTeachingSuggestion',
                component: () => import('@/views/admin/learningAnalysis/suggestion.vue')
            },
            // ✨✨✨✨✨✨✨✨✨✨✨✨✨✨✨✨
            {
                path: 'student/learning-detail',
                name: 'StudentLearningDetail',
                component: () => import('@/views/admin/student/learningDetail.vue')
            },
            {
                path: 'login-log',
                name: 'AdminLoginLog',
                component: AdminLoginLog
            },
            {
                path: 'question',
                name: 'AdminQuestion',
                component: AdminQuestion
            },
            {
                path: 'online-monitor',
                name: 'AdminOnlineMonitor',
                component: () => import('@/views/admin/onlineMonitor/index.vue')
            },
            {
                path: 'student/wrong-history',
                name: 'AdminStudentWrongHistory',
                component: () => import('@/views/student/wrongQuestion/history.vue')
            }
        ]
    },
    // ================= PC 学员端 =================
    {
        path: '/student',
        component: StudentLayout,
        redirect: '/student/home',
        children: [
            { path: 'home', component: StudentHome },
            { path: 'wrong-list', component: () => import('@/views/student/wrongQuestion/index.vue') },
            { path: 'practice/session/list', component: () => import('@/views/student/practice/sessionList.vue') },
            { path: 'practice/session/question', component: () => import('@/views/student/practice/sessionQuestion.vue') },
            { path: 'exam/list', component: () => import('@/views/student/exam/examList.vue') },
            { path: 'exam/question', component: () => import('@/views/student/exam/examQuestion.vue') },
            { path: 'exam/result', component: () => import('@/views/student/exam/examResult.vue') },
            { path: 'wrong-question/history', component: () => import('@/views/student/wrongQuestion/history.vue') },
            { path: 'wrong-question/review', component: () => import('@/views/student/wrongQuestion/review.vue') }
        ]
    },
    // ================= 移动端 后台 =================
    {
        path: '/m/admin',
        component: MobileAdminLayout,
        redirect: '/m/admin/student',
        children: [
            {
                path: 'student',
                name: 'MobileAdminStudent',
                component: MobileAdminStudent
            },
            {
                path: 'student/learning-detail',
                name: 'MobileAdminStudentLearningDetail',
                component: () => import('@/views/mobile/admin/student/learningDetail.vue')
            },
            {
                path: 'learning-analysis',
                name: 'MobileAdminLearningAnalysis',
                component: MobileAdminLearningAnalysis
            },
            // ✨✨✨ 新增：教学建议 (移动端，复用PC组件即可，因为你用的是Element UI表格，手机上看可能需要横向滑动，但功能是一样的) ✨✨✨
            {
                path: 'teaching-suggestion',
                name: 'MobileAdminTeachingSuggestion',
                component: () => import('@/views/admin/learningAnalysis/suggestion.vue')
            },
            // ✨✨✨✨✨✨✨✨✨✨✨✨✨✨✨✨
            {
                path: 'me',
                name: 'MobileAdminMe',
                component: MobileAdminMe
            }
        ]
    },
    // ================= 移动端 学员端 =================
    {
        path: '/m/student',
        component: MobileStudentLayout,
        redirect: '/m/student/home',
        children: [
            {
                path: 'home',
                name: 'MobileStudentHome',
                component: MobileStudentHome
            },
            {
                path: 'practice',
                name: 'MobileStudentPractice',
                component: () => import('@/views/mobile/student/practice/sessionList.vue')
            },
            {
                path: 'practice/question',
                name: 'MobilePracticeQuestion',
                component: () => import('@/views/mobile/student/practice/sessionQuestion.vue'),
                meta: {
                    hideTabbar: true
                }
            },
            {
                path: 'exam',
                name: 'MobileStudentExam',
                component: () => import('@/views/mobile/student/exam/examList.vue')
            },
            {
                path: 'exam/question',
                name: 'MobileExamQuestion',
                component: () => import('@/views/mobile/student/exam/examQuestion.vue'),
                meta: {
                    hideTabbar: true
                }
            },
            {
                path: 'exam/result',
                name: 'MobileExamResult',
                component: () => import('@/views/mobile/student/exam/examResult.vue'),
                meta: {
                    hideTabbar: true
                }
            },
            {
                path: 'wrong',
                name: 'MobileStudentWrong',
                component: () => import('@/views/mobile/student/wrongQuestion/index.vue')
            },
            {
                path: 'wrong/review',
                name: 'MobileWrongQuestionReview',
                component: () => import('@/views/mobile/student/wrongQuestion/review.vue'),
                meta: {
                    hideTabbar: true
                }
            },
            {
                path: 'me',
                name: 'MobileStudentMe',
                component: MobileStudentMe
            }
        ]
    }
]

const router = new VueRouter({
    mode: 'history',
    routes
})

// === 移动端重定向逻辑 ===
function getMobileRedirectPath(path, role) {
    if (role === 'ADMIN') {
        if (path.startsWith('/admin/student/learning-detail')) {
            return '/m/admin/student/learning-detail'
        }
        if (path.startsWith('/admin/learning-analysis')) {
            return '/m/admin/learning-analysis'
        }
        // ✨ 新增教学建议的移动端跳转映射
        if (path.startsWith('/admin/teaching-suggestion')) {
            return '/m/admin/teaching-suggestion'
        }
        if (path.startsWith('/admin')) {
            return '/m/admin/student'
        }
    }
    // ... 学生端保持不变 ...
    if (role === 'STUDENT') {
        if (path.startsWith('/student/practice/session/question')) {
            return '/m/student/practice/question'
        }
        if (path.startsWith('/student/practice')) {
            return '/m/student/practice'
        }
        if (path.startsWith('/student/exam/result')) {
            return '/m/student/exam/result'
        }
        if (path.startsWith('/student/exam/question')) {
            return '/m/student/exam/question'
        }
        if (path.startsWith('/student/exam')) {
            return '/m/student/exam'
        }
        if (path.startsWith('/student/wrong-question/review')) {
            return '/m/student/wrong/review'
        }
        if (
            path.startsWith('/student/wrong-list') ||
            path.startsWith('/student/wrong-question')
        ) {
            return '/m/student/wrong'
        }
        if (path.startsWith('/student')) {
            return '/m/student/home'
        }
    }
    return null
}

// === PC端重定向逻辑 ===
function getPcRedirectPath(path, role) {
    if (role === 'ADMIN') {
        if (path.startsWith('/m/admin/student/learning-detail')) {
            return '/admin/student/learning-detail'
        }
        if (path.startsWith('/m/admin/learning-analysis')) {
            return '/admin/learning-analysis'
        }
        // ✨ 新增教学建议的PC端跳转映射
        if (path.startsWith('/m/admin/teaching-suggestion')) {
            return '/admin/teaching-suggestion'
        }
        if (path.startsWith('/m/admin')) {
            return '/admin/home'
        }
    }
    // ... 学生端保持不变 ...
    if (role === 'STUDENT') {
        if (path.startsWith('/m/student/practice/question')) {
            return '/student/practice/session/question'
        }
        if (path.startsWith('/m/student/practice')) {
            return '/student/practice/session/list'
        }
        if (path.startsWith('/m/student/exam/result')) {
            return '/student/exam/result'
        }
        if (path.startsWith('/m/student/exam/question')) {
            return '/student/exam/question'
        }
        if (path.startsWith('/m/student/exam')) {
            return '/student/exam/list'
        }
        if (path.startsWith('/m/student/wrong/review')) {
            return '/student/wrong-question/review'
        }
        if (path.startsWith('/m/student/wrong')) {
            return '/student/wrong-list'
        }
        if (path.startsWith('/m/student')) {
            return '/student/home'
        }
    }
    return null
}

const whiteList = ['/login', '/m/login']

router.beforeEach(async (to, from, next) => {
    const token = getToken()
    let userInfo = store.state.userInfo
    const currentClientType = getClientType()
    const targetClientType = getClientTypeByRoutePath(to.path)

    // 1. 已登录，有 token
    if (token) {
        // 如果 Vuex 中没有用户信息，先去拉取
        if (!userInfo) {
            try {
                const res = await getCurrentUser()
                // 已经在 request.js 统一处理过 401 了
                if (res && res._handled401) {
                    return next(false)
                }
                if (res.code === 200) {
                    store.commit('SET_USER_INFO', res.data)
                    userInfo = res.data
                } else {
                    store.commit('CLEAR_USER_INFO')
                    return next(false)
                }
            } catch (e) {
                console.log('获取用户信息异常', e)
                store.commit('CLEAR_USER_INFO')
                return next(false)
            }
        }

        const role = userInfo?.role

        // 如果用户已登录但访问的是登录页，重定向到对应的首页
        if (to.path === '/login' || to.path === '/m/login') {
            return next(getHomePathByRole(role, currentClientType))
        }

        // 设备类型拦截：如果当前是 mobile 环境，但访问了 PC 路由
        if (currentClientType === 'mobile') {
            const mobileRedirectPath = getMobileRedirectPath(to.path, role)
            if (mobileRedirectPath && mobileRedirectPath !== to.path) {
                return next({
                    path: mobileRedirectPath,
                    query: to.query,
                    replace: true
                })
            }
        }

        // 设备类型拦截：如果当前是 pc 环境，但访问了 mobile 路由
        if (currentClientType === 'pc') {
            const pcRedirectPath = getPcRedirectPath(to.path, role)
            if (pcRedirectPath && pcRedirectPath !== to.path) {
                return next({
                    path: pcRedirectPath,
                    query: to.query,
                    replace: true
                })
            }
        }

        // 权限拦截：判断角色与路由是否匹配 (PC端)
        if (to.path.startsWith('/admin') && role !== 'ADMIN') {
            return next(false)
        }
        // 权限拦截：判断角色与路由是否匹配 (PC端)
        if (to.path.startsWith('/student') && role !== 'STUDENT') {
            return next(false)
        }

        // 权限拦截：判断角色与路由是否匹配 (移动端)
        if (to.path.startsWith('/m/admin') && role !== 'ADMIN') {
            return next(false)
        }
        // 权限拦截：判断角色与路由是否匹配 (移动端)
        if (to.path.startsWith('/m/student') && role !== 'STUDENT') {
            return next(false)
        }

        return next()
    }

    // 2. 未登录，无 token
    if (whiteList.includes(to.path)) {
        // 如果当前是 mobile 环境，但访问了 PC 登录页，则重定向到 mobile 登录页
        if (to.path === '/login' && currentClientType === 'mobile') {
            return next('/m/login')
        }
        // 如果当前是 PC 环境，但访问了 mobile 登录页，则重定向到 PC 登录页
        if (to.path === '/m/login' && currentClientType === 'pc') {
            return next('/login')
        }
        return next()
    }

    return next(getLoginPathByRoutePath(to.path))
})

export default router